import React from 'react';
import {
  Package, UtensilsCrossed, Users, DollarSign, Clock, CheckCircle, ChefHat, Truck,
  TrendingUp, Plus, Edit, Trash2, GripVertical, ArrowUp, ArrowDown, FolderOpen,
  Upload, Image, Star, MessageSquare, Mail, Eye, Reply, Send, Megaphone, Tag, Calendar, X, ChevronLeft
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { apiRequest, formatPrice, getAuthToken } from '../../lib/utils';

const OrdersTab = ({
  deleteOrder,
  getStatusColor,
  orders,
  selectedOrderDate,
  setSelectedOrderDate,
  updateOrderStatus,
}) => (
          <TabsContent value="orders">
            {/* Date-wise Summary */}
            {(() => {
              // Group orders by date and calculate summaries
              const ordersByDate = orders.reduce((acc, order) => {
                const date = order.created_at ? new Date(order.created_at).toLocaleDateString('en-GB') : 'Unknown';
                if (!acc[date]) {
                  acc[date] = { orders: [], totalAmount: 0, totalItems: 0, itemsSummary: {} };
                }
                acc[date].orders.push(order);
                acc[date].totalAmount += order.total || 0;
                
                // Build items summary
                order.items?.forEach(item => {
                  const itemName = item.name || 'Unknown';
                  if (!acc[date].itemsSummary[itemName]) {
                    acc[date].itemsSummary[itemName] = { quantity: 0, revenue: 0 };
                  }
                  acc[date].itemsSummary[itemName].quantity += item.quantity || 0;
                  acc[date].itemsSummary[itemName].revenue += (item.price * item.quantity) || 0;
                  acc[date].totalItems += item.quantity || 0;
                });
                
                return acc;
              }, {});
              
              const sortedDates = Object.keys(ordersByDate).sort((a, b) => {
                const dateA = a.split('/').reverse().join('-');
                const dateB = b.split('/').reverse().join('-');
                return new Date(dateB) - new Date(dateA);
              });
              
              // Calculate totals for all time
              const allTimeTotals = orders.reduce((acc, order) => {
                acc.totalOrders += 1;
                acc.totalAmount += order.total || 0;
                acc.totalItems += order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
                return acc;
              }, { totalOrders: 0, totalAmount: 0, totalItems: 0 });
              
              // Filter orders if date is selected
              const filteredOrders = selectedOrderDate 
                ? ordersByDate[selectedOrderDate]?.orders || []
                : orders;
              
              const selectedDateData = selectedOrderDate ? ordersByDate[selectedOrderDate] : null;
              
              return (
                <>
                  {/* All-time Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="bg-[#1C1C22] border-zinc-700">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-zinc-400">Total Orders</p>
                            <p className="text-3xl font-bold text-white">{allTimeTotals.totalOrders}</p>
                          </div>
                          <Package className="h-10 w-10 text-[#FFC107]" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#1C1C22] border-zinc-700">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-zinc-400">Total Items Sold</p>
                            <p className="text-3xl font-bold text-white">{allTimeTotals.totalItems}</p>
                          </div>
                          <UtensilsCrossed className="h-10 w-10 text-green-500" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-[#1C1C22] border-zinc-700">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-zinc-400">Total Revenue</p>
                            <p className="text-3xl font-bold text-[#FFC107]">{formatPrice(allTimeTotals.totalAmount)}</p>
                          </div>
                          <DollarSign className="h-10 w-10 text-[#FFC107]" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Date-wise Breakdown */}
                  <Card className="bg-[#141417] border-[#27272A] mb-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-[#FFC107]" />
                        Daily Summary
                        <span className="text-sm font-normal text-zinc-400 ml-2">(Click date to view details)</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-[#27272A]">
                            <TableHead>Date</TableHead>
                            <TableHead className="text-center">Orders</TableHead>
                            <TableHead className="text-center">Items Sold</TableHead>
                            <TableHead className="text-right">Total Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedDates.slice(0, 10).map((date) => (
                            <TableRow 
                              key={date} 
                              className={`border-[#27272A] cursor-pointer transition-colors hover:bg-[#25252E] ${selectedOrderDate === date ? 'bg-[#FFC107]/20 hover:bg-[#FFC107]/30' : ''}`}
                              onClick={() => setSelectedOrderDate(selectedOrderDate === date ? null : date)}
                            >
                              <TableCell className="font-medium">
                                <div className="flex items-center gap-2">
                                  <Calendar className={`h-4 w-4 ${selectedOrderDate === date ? 'text-[#FFC107]' : 'text-zinc-500'}`} />
                                  {date}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge variant="outline" className="border-zinc-600">{ordersByDate[date].orders.length}</Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className="bg-green-500/20 text-green-500">{ordersByDate[date].totalItems}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-semibold text-[#FFC107]">
                                {formatPrice(ordersByDate[date].totalAmount)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                  
                  {/* Selected Date Details */}
                  {selectedOrderDate && selectedDateData && (
                    <Card className="bg-[#141417] border-[#FFC107]/50 mb-6">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-[#FFC107]" />
                            {selectedOrderDate} - Items Sold Summary
                          </CardTitle>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedOrderDate(null)}
                            className="text-zinc-400 hover:text-white"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Clear Filter
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                          <div className="bg-[#1C1C22] rounded-lg p-4">
                            <p className="text-sm text-zinc-400">Orders on this day</p>
                            <p className="text-2xl font-bold text-white">{selectedDateData.orders.length}</p>
                          </div>
                          <div className="bg-[#1C1C22] rounded-lg p-4">
                            <p className="text-sm text-zinc-400">Items Sold</p>
                            <p className="text-2xl font-bold text-green-500">{selectedDateData.totalItems}</p>
                          </div>
                          <div className="bg-[#1C1C22] rounded-lg p-4">
                            <p className="text-sm text-zinc-400">Revenue</p>
                            <p className="text-2xl font-bold text-[#FFC107]">{formatPrice(selectedDateData.totalAmount)}</p>
                          </div>
                        </div>
                        
                        <h4 className="font-semibold mb-3 text-white">Items Breakdown:</h4>
                        <Table>
                          <TableHeader>
                            <TableRow className="border-[#27272A]">
                              <TableHead>Item Name</TableHead>
                              <TableHead className="text-center">Quantity Sold</TableHead>
                              <TableHead className="text-right">Revenue</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {Object.entries(selectedDateData.itemsSummary)
                              .sort((a, b) => b[1].quantity - a[1].quantity)
                              .map(([itemName, data]) => (
                                <TableRow key={itemName} className="border-[#27272A]">
                                  <TableCell className="font-medium">{itemName}</TableCell>
                                  <TableCell className="text-center">
                                    <Badge className="bg-blue-500/20 text-blue-400">{data.quantity}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-[#FFC107]">
                                    {formatPrice(data.revenue)}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Orders List */}
                  <Card className="bg-[#141417] border-[#27272A]">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{selectedOrderDate ? `Orders on ${selectedOrderDate}` : 'All Orders'}</span>
                        {selectedOrderDate && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedOrderDate(null)}
                            className="border-zinc-700"
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Show All Orders
                          </Button>
                        )}
                      </CardTitle>
                    </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#27272A]">
                      <TableHead>Order ID</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id} className="border-[#27272A]" data-testid={`admin-order-${order.id}`}>
                        <TableCell className="font-mono">{order.id?.slice(0, 8)}</TableCell>
                        <TableCell>
                          <div>
                            <p>{order.user_name}</p>
                            <p className="text-xs text-zinc-500">{order.user_email}</p>
                            {order.scheduled_date && order.scheduled_time && (
                              <p className="text-xs text-[#FFC107] font-medium mt-1">🕐 {order.scheduled_date} {order.scheduled_time}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{order.user_phone}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {order.items?.map((item, i) => (
                              <div key={i}>
                                {item.quantity}x {item.name}
                                {item.selected_size && (
                                  <span className="text-zinc-400 text-xs ml-1">({item.selected_size === 'big' ? 'Big' : 'Normal'})</span>
                                )}
                                {item.has_combo && (
                                  <span className="text-green-400 text-xs block ml-4">+ Combo (Drink + Fries)</span>
                                )}
                                {item.selected_addons && item.selected_addons.length > 0 && (
                                  <span className="text-[#FFC107]/70 text-xs block ml-4">+ {item.selected_addons.map(a => a.name).join(', ')}</span>
                                )}
                                {item.removed_ingredients && item.removed_ingredients.length > 0 && (
                                  <span className="text-red-400 text-xs block ml-4">Without: {item.removed_ingredients.join(', ')}</span>
                                )}
                                {item.special_instructions && (
                                  <span className="text-zinc-400 text-xs block ml-4">Note: {item.special_instructions}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <Badge variant="outline" className="border-zinc-700">{order.payment_method}</Badge>
                            <Badge className={order.payment_status === 'paid' ? 'ml-2 bg-green-500/20 text-green-500' : 'ml-2 bg-yellow-500/20 text-yellow-500'}>
                              {order.payment_status}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#FFC107] font-semibold">{formatPrice(order.total)}</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(order.order_status)}>
                            {order.order_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Select
                              value={order.order_status}
                              onValueChange={(value) => updateOrderStatus(order.id, value)}
                            >
                              <SelectTrigger className="w-32 bg-[#1C1C22] border-zinc-700" data-testid={`status-select-${order.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#141417] border-[#27272A]">
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="preparing">Preparing</SelectItem>
                                <SelectItem value="ready">Ready</SelectItem>
                                <SelectItem value="delivered">Delivered</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              onClick={() => deleteOrder(order.id)}
                              data-testid={`delete-order-${order.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
                </>
              );
            })()}
          </TabsContent>
);

export default OrdersTab;
